/**
 * Tagging a colleague in a note, and answering one.
 *
 * Jonathon, 2026-08-25: "I want to be able to tag other reps in notes and be
 * able to reply inside of roofboard to each other. I want the note tags and
 * replies to go to the reps email as well so we don't miss it."
 *
 * The handle is the person's own name — `@Craig Hurst` — rather than a login or
 * an invented username. Nobody in this company has a username, and a rep typing
 * a note in a driveway is not going to learn one.
 *
 * That makes the parsing the interesting part: a handle can contain a space, so
 * a name is matched against the staff list rather than against a pattern. What
 * the list does not contain is not a tag, which is the right way round — an
 * email address in a note ("@gmail") tags nobody.
 */

/** The little of a profile that tagging depends on. */
export interface Mentionable {
  id: string;
  /** Null in the database, and a person with no name cannot be tagged. */
  full_name: string | null;
  email?: string | null;
  active?: boolean | null;
}

/** A person's name, or the empty string — they are filtered out either way. */
const nameOf = (person: Mentionable): string => person.full_name?.trim() ?? "";

/** Staff who can be tagged: everybody still here, longest name first. */
function taggable(people: readonly Mentionable[]): Mentionable[] {
  return (
    people
      .filter((person) => person.active !== false && person.full_name?.trim())
      // Longest first so "@Craig Hurst" is one tag rather than a tag on "Craig"
      // followed by the word "Hurst".
      .slice()
      .sort((a, b) => nameOf(b).length - nameOf(a).length)
  );
}

/** The handles one person answers to: their full name, and their first name. */
function handlesFor(
  person: Mentionable,
  people: readonly Mentionable[],
): string[] {
  const full = nameOf(person);
  const first = full.split(/\s+/)[0] ?? "";
  // A first name is only a handle when it belongs to one person. Two Chrises
  // and "@Chris" is a guess, so it stops being a handle for either of them.
  const unique =
    first.length > 1 &&
    people.filter(
      (other) =>
        other.active !== false &&
        (nameOf(other).split(/\s+/)[0] ?? "").toLowerCase() ===
          first.toLowerCase(),
    ).length === 1;
  return unique && first.toLowerCase() !== full.toLowerCase()
    ? [full, first]
    : [full];
}

/** Is the character after a handle the end of it? */
const endsHandle = (char: string | undefined): boolean =>
  char === undefined || !/[a-z0-9'-]/i.test(char);

/**
 * One piece of a note: plain text, or a tag on somebody.
 *
 * The panel draws these — the tag as a chip, the rest as it was typed.
 */
export interface MentionSegment {
  text: string;
  /** `profiles.id` when this piece is a tag, null when it is prose. */
  profileId: string | null;
}

/** A note broken into what it says and who it tags. */
export function mentionSegments(
  body: string,
  people: readonly Mentionable[],
): MentionSegment[] {
  const candidates = taggable(people);
  const segments: MentionSegment[] = [];
  let plain = "";

  const flush = () => {
    if (plain) segments.push({ text: plain, profileId: null });
    plain = "";
  };

  let at = 0;
  while (at < body.length) {
    if (body[at] !== "@") {
      plain += body[at];
      at += 1;
      continue;
    }

    const after = body.slice(at + 1);
    const hit = candidates
      .flatMap((person) =>
        handlesFor(person, candidates).map((handle) => ({ person, handle })),
      )
      .find(
        ({ handle }) =>
          after.slice(0, handle.length).toLowerCase() ===
            handle.toLowerCase() && endsHandle(after[handle.length]),
      );

    if (!hit) {
      plain += body[at];
      at += 1;
      continue;
    }

    flush();
    segments.push({
      text: `@${after.slice(0, hit.handle.length)}`,
      profileId: hit.person.id,
    });
    at += 1 + hit.handle.length;
  }

  flush();
  return segments;
}

/** Everybody tagged in a note, in the order they appear, without repeats. */
export function parseMentions(
  body: string,
  people: readonly Mentionable[],
): string[] {
  const ids: string[] = [];
  for (const segment of mentionSegments(body, people)) {
    if (segment.profileId && !ids.includes(segment.profileId)) {
      ids.push(segment.profileId);
    }
  }
  return ids;
}

/**
 * The `@…` being typed at the caret, if there is one.
 *
 * `from` is where the `@` sits, so the picker can replace exactly what was
 * typed. Null once the word has a space in it and matches nobody — otherwise
 * the picker would hang open for the rest of the sentence.
 */
export function mentionQueryAt(
  text: string,
  caret: number,
): { query: string; from: number } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at === -1) return null;
  // An @ in the middle of a word is an email address, not a tag.
  if (at > 0 && /[a-z0-9]/i.test(before[at - 1] ?? "")) return null;
  const query = before.slice(at + 1);
  if (/\n/.test(query)) return null;
  // A name is two words at most before it is plainly not a name any more.
  if (query.split(" ").length > 2) return null;
  return { query, from: at };
}

/** Staff matching what has been typed after the `@`, best first. */
export function mentionCandidates(
  people: readonly Mentionable[],
  query: string,
  limit = 6,
): Mentionable[] {
  const needle = query.trim().toLowerCase();
  const live = people
    .filter((person) => person.active !== false && person.full_name?.trim())
    .slice()
    .sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
  if (!needle) return live.slice(0, limit);
  return (
    live
      .filter((person) => nameOf(person).toLowerCase().includes(needle))
      // Somebody whose name *starts* with what was typed is who was meant.
      .sort((a, b) => {
        const rank = (person: Mentionable) =>
          nameOf(person).toLowerCase().startsWith(needle) ? 0 : 1;
        return rank(a) - rank(b) || nameOf(a).localeCompare(nameOf(b));
      })
      .slice(0, limit)
  );
}

/**
 * Replacing the half-typed `@…` with the person who was picked.
 *
 * Returns the whole note and where the caret should land — after the trailing
 * space, so the next word is typed rather than the name being retyped.
 */
export function applyMention(
  text: string,
  from: number,
  caret: number,
  person: Mentionable,
): { text: string; caret: number } {
  const inserted = `@${nameOf(person)} `;
  return {
    text: text.slice(0, from) + inserted + text.slice(caret),
    caret: from + inserted.length,
  };
}

/** What a tagged rep is sent. */
export interface MentionEmail {
  subject: string;
  text: string;
}

/** The little of a job that naming it depends on. */
export interface LabelledJob {
  job_number?: number | null;
  address?: string | null;
  /** Present when the query joined the customer. */
  contact?: { full_name?: string | null } | null;
}

/**
 * "#1042 — Cat Edwards, 1428 Elm St", the way a person says which job.
 *
 * The customer's name is in there because that is how everybody in this company
 * refers to a job out loud. Jonathon, 2026-09-21: "tagged notes should mention
 * the customers name." A tag arrives as a push notification on a phone, where
 * the subject line is most of what gets read, and "#1042 — 1428 Elm St" made
 * the reader open the app to find out whose roof they were being asked about.
 * Two of the three — the number and the address — are things you look up; the
 * name is the thing you already know.
 *
 * Whatever is missing is left out rather than leaving a stray comma or dash
 * behind, and a job with none of the three is "a job", because an empty subject
 * line is worse than a vague one.
 */
export function jobLabel(job: LabelledJob): string {
  const who = job.contact?.full_name?.trim() ?? "";
  const where = job.address?.trim() ?? "";
  // The name and the address are the same fact about the same doorstep, so they
  // travel together under the job number.
  const what = [who, where].filter(Boolean).join(", ");
  const label = [job.job_number ? `#${job.job_number}` : null, what]
    .filter(Boolean)
    .join(" — ");
  return label || "a job";
}

/**
 * The email behind a tag.
 *
 * Jonathon: "the email body would just say what the note is and give the option
 * to click into roofboard and reply." So it is the note, the job it is on, and
 * a link — and nothing else. It is deliberately not a copy of the whole thread:
 * the thread is in Roof Board, which is where the answer goes.
 *
 * Plain text on purpose. It is read on a phone, half of it in a notification
 * preview, and there is nothing here that markup would add.
 */
export function mentionEmail(input: {
  /** Who wrote the note. */
  author: string;
  /** The job, as a person would say it: "#1042 — 1428 Elm St". */
  job: string;
  /** The note itself, as typed. */
  body: string;
  /** Deep link to the job in Roof Board. */
  url: string;
  /** True when this is an answer in a thread rather than a new note. */
  isReply: boolean;
}): MentionEmail {
  const what = input.isReply ? "replied to a note" : "tagged you in a note";
  return {
    subject: `${input.author} ${input.isReply ? "replied on" : "tagged you on"} ${input.job}`,
    text: [
      `${input.author} ${what} on ${input.job}.`,
      "",
      input.body.trim(),
      "",
      `Open it in Roof Board to reply: ${input.url}`,
      "",
      "You are getting this because you were tagged, or because you are in the conversation.",
    ].join("\n"),
  };
}

/**
 * Who hears about a note.
 *
 * Everybody tagged in it, plus — when it is a reply — whoever wrote the note
 * being answered and everybody already in that thread. A person is never
 * emailed about their own note, and somebody with no email address on their
 * profile is not a recipient at all.
 */
export function mentionRecipients(input: {
  /** Ids tagged in the note just written. */
  mentions: readonly string[];
  /** Everybody already in the thread: its author, and its repliers. */
  thread?: readonly string[] | undefined;
  /** Who wrote it. Never told about their own note. */
  author: string | null;
  people: readonly Mentionable[];
}): Mentionable[] {
  const wanted = [...input.mentions, ...(input.thread ?? [])];
  const seen = new Set<string>();
  const out: Mentionable[] = [];
  for (const id of wanted) {
    if (!id || id === input.author || seen.has(id)) continue;
    seen.add(id);
    const person = input.people.find((p) => p.id === id);
    // A deactivated profile is not a person any more. The picker has always
    // refused to offer one, but that only stops NEW tags: somebody tagged
    // before they were turned off, or sitting in a thread they replied to
    // months ago, went on being emailed for ever. Jonathon, 2026-09-08:
    // Lysa's "tagged notes are still going to her personal email even though
    // I've deactivated her personal one."
    if (person?.active === false) continue;
    if (person?.email?.trim()) out.push(person);
  }
  return out;
}
